const FOCUSABLE_SELECTOR = [
    'a[href]',
    'area[href]',
    'button:not([disabled])',
    'input:not([disabled])',
    'select:not([disabled])',
    'textarea:not([disabled])',
    'iframe',
    '[tabindex]:not([tabindex="-1"])',
    '[contenteditable="true"]',
].join(', ');

export default {
    cookieBanner: null,
    mutationObserver: null,
    activeTrigger: null,
    isTrapActive: false,

    init() {
        this.cookieBanner = document.getElementById('cookie-law-info-bar');
        if (!this.cookieBanner) return;

        // Le reste de la page est rendu inert (voir activateFocusTrap) tant que le bandeau
        // est affiché : l'interaction est donc bien confinée au bandeau, aria-modal="true"
        // est correct ici.
        this.cookieBanner.setAttribute('role', 'dialog');
        this.cookieBanner.setAttribute('aria-modal', 'true');
        this.cookieBanner.setAttribute('aria-labelledby', 'wt-cli-cookie-banner-title');

        this.bindConsentManagerLinks();
        this.observeBannerState();
        this.syncFocusTrapState(true);
    },

    bindConsentManagerLinks() {
        const consentManagerLinks = document.querySelectorAll('[data-js="show-consent-manager"]');
        if (!consentManagerLinks.length) return;

        consentManagerLinks.forEach((link) => {
            link.addEventListener('click', (event) => {
                event.preventDefault();
                this.activeTrigger = event.currentTarget;
                this.cookieBanner.style.display = 'block';
                this.syncFocusTrapState(true);
            });
        });
    },

    observeBannerState() {
        this.mutationObserver = new MutationObserver(() => {
            this.syncFocusTrapState();
        });

        this.mutationObserver.observe(this.cookieBanner, {
            attributes: true,
            attributeFilter: ['style', 'class', 'hidden', 'aria-hidden'],
            childList: true,
            subtree: true,
        });
    },

    isBannerVisible() {
        if (!this.cookieBanner) return false;

        const computedStyle = window.getComputedStyle(this.cookieBanner);

        return (
            computedStyle.display !== 'none' &&
            computedStyle.visibility !== 'hidden' &&
            this.cookieBanner.getAttribute('aria-hidden') !== 'true'
        );
    },

    getFocusableElements() {
        if (!this.cookieBanner) return [];

        return Array.from(this.cookieBanner.querySelectorAll(FOCUSABLE_SELECTOR)).filter((element) => {
            const computedStyle = window.getComputedStyle(element);

            return (
                computedStyle.display !== 'none' &&
                computedStyle.visibility !== 'hidden' &&
                !element.hasAttribute('hidden') &&
                !element.closest('[hidden], [aria-hidden="true"]')
            );
        });
    },

    handleKeydown(event) {
        if (!this.isTrapActive) return;

        if (event.key === 'Escape') {
            const rejectBtn = this.cookieBanner.querySelector('.cookie_action_close_header_reject, #wt-cli-reject-btn');
            if (rejectBtn) {
                rejectBtn.click();
            } else {
                this.deactivateFocusTrap();
            }
            return;
        }

        if (event.key !== 'Tab') return;

        const focusableElements = this.getFocusableElements();
        if (!focusableElements.length) {
            event.preventDefault();
            this.cookieBanner?.focus();
            return;
        }

        const firstElement = focusableElements[0];
        const lastElement = focusableElements[focusableElements.length - 1];
        const activeElement = document.activeElement;

        // Focus hors du bandeau (ex : chargement initial) — on le ramène à l'intérieur
        if (!this.cookieBanner.contains(activeElement)) {
            event.preventDefault();
            event.shiftKey ? lastElement.focus() : firstElement.focus();
            return;
        }

        if (event.shiftKey && activeElement === firstElement) {
            event.preventDefault();
            lastElement.focus();
            return;
        }

        if (!event.shiftKey && activeElement === lastElement) {
            event.preventDefault();
            firstElement.focus();
        }
    },

    activateFocusTrap(shouldMoveFocus = false) {
        if (!this.cookieBanner || this.isTrapActive) {
            if (shouldMoveFocus) {
                this.focusFirstElement();
            }
            return;
        }

        this.isTrapActive = true;
        this.cookieBanner.setAttribute('tabindex', '-1');
        this.boundHandleKeydown = this.handleKeydown.bind(this);
        document.addEventListener('keydown', this.boundHandleKeydown);

        // Retirer du tab order ET de l'arbre AT via inert (contrairement à aria-hidden,
        // inert empêche physiquement la navigation clavier vers ces éléments)
        const bannerRoot = this.cookieBanner.closest('body > *') ?? this.cookieBanner;
        document.querySelectorAll('body > *').forEach((el) => {
            if (el !== bannerRoot && el.tagName !== 'SCRIPT' && el.tagName !== 'STYLE' && !el.hasAttribute('inert')) {
                el.setAttribute('inert', '');
                el.setAttribute('data-cookie-inert', '');
            }
        });

        if (shouldMoveFocus) {
            this.focusFirstElement();
        }
    },

    deactivateFocusTrap() {
        if (!this.isTrapActive) return;

        this.isTrapActive = false;

        if (this.boundHandleKeydown) {
            document.removeEventListener('keydown', this.boundHandleKeydown);
            this.boundHandleKeydown = null;
        }

        this.cookieBanner?.removeAttribute('tabindex');

        // Restaurer les éléments mis en inert à l'activation
        document.querySelectorAll('[data-cookie-inert]').forEach((el) => {
            el.removeAttribute('inert');
            el.removeAttribute('data-cookie-inert');
        });

        if (this.activeTrigger instanceof HTMLElement) {
            this.activeTrigger.focus();
            this.activeTrigger = null;
        } else {
            const mainContent = document.querySelector('main, [role="main"]');
            const firstFocusable = mainContent?.querySelector(FOCUSABLE_SELECTOR);
            firstFocusable?.focus();
        }
    },

    focusFirstElement() {
        const focusableElements = this.getFocusableElements();
        const firstElement = focusableElements[0];

        if (firstElement) {
            firstElement.focus();
            return;
        }

        this.cookieBanner?.focus();
    },

    syncFocusTrapState(shouldMoveFocus = false) {
        if (this.isBannerVisible()) {
            this.activateFocusTrap(shouldMoveFocus);
            return;
        }

        this.deactivateFocusTrap();
    },
};
